
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { useAuthActions } from "@contexts/auth";
import { cloneElement, Fragment, useEffect, useRef, useState } from "react";
import { BRAZILIAN_STATES } from "@utils/brazilianStates";
import { fetchCitiesByState } from "@utils/brazilianCities";
import InfoTooltip from "@components/InfoTooltip";
import BuildingAccessPanel from "./BuildingAccessPanel";
import { fetchMegaProjects } from "./integrationsApi";

const REQUIRED_FIELDS = ['company_id', 'building_code', 'company_code', 'building_name'];

// Índice usado para corrigir o orçamento. CUB é publicado por Sinduscon estadual — por isso
// exige UF; INCC é nacional, não varia por estado.
const INDEXER_FAMILIES = ['CUB', 'INCC'];

// Categorias padronizadas de projeto-padrão do CUB (NBR 12721 / Sinduscon).
const CUB_CATEGORIES = ['Residencial', 'Comercial', 'Industrial', 'Galpão Industrial'];

// Sienge classifica movimentos bancários por um id numérico; esses 4 tipos (transferência
// entre contas, IOF, etc.) são rotineiramente excluídos da reconciliação financeira e por
// isso valem como default editável — mas só faz sentido para construtoras Sienge.
const DEFAULT_SIENGE_BANK_MOVEMENT_IDS = "14,16,21,22";

// Toda obra cadastrada pelo Backoffice é MC (monitoramento e controle) — o campo Scope deixou
// de ser exibido no formulário, mas o valor continua sendo enviado ao back-end.
const FIXED_SCOPE = 'mc';

// Rótulos em português — o VALOR salvo no banco continua em inglês (contrato já usado pelo
// resto do sistema); só o texto exibido ao operador é traduzido.
const FIELD_LABELS = {
  building_code: 'Código da Obra',
  company_code: 'Código da Construtora',
  building_name: 'Nome da Obra',
  city: 'Cidade',
  state: 'UF',
  start_date: 'Data de Início',
  end_date: 'Data de Término',
  area: 'Área',
  budget_version: 'Versão do Orçamento',
  building_constructive_units_id: 'Unidades Construtivas',
  cost_centers_id: 'Centros de Custo',
  building_cost_centers_id: 'Centros de Custo da Obra',
  appropriation_level: 'Nível de Apropriação',
  plan_tool: 'Ferramenta de Planejamento',
  bank_movement_id_to_remove: 'IDs de Movimento Bancário a Excluir',
  cost_centers_id_comercial: 'Centros de Custo Comercial',
  bill_id_to_remove: 'IDs de Título a Excluir',
  cnpj: 'CNPJ da Obra',
  // Curtos para caberem numa linha ao lado do CNPJ (D11); o nome inteiro fica no `title`.
  projeto_reduzido: 'Cód. reduzido do projeto (MEGA)',
  projeto_id: 'ID do projeto (MEGA)',
};

const FIELD_FULL_LABELS = {
  projeto_reduzido: 'Código Reduzido do Projeto (MEGA)',
  projeto_id: 'ID do Projeto (MEGA)',
};


// Orientação por ERP para campos cujo significado difere entre Sienge e MEGA — levantamento em
// `openspec/changes/add-mega-building-registration-fields/field-audit.md`.
// `cost_centers_id`/`cost_centers_id_comercial`/`plan_tool` não têm hint aqui porque nem
// aparecem no formulário quando a construtora é MEGA — ver `siengeOnly` em `fields`.
const ERP_FIELD_HINTS = {
  MEGA: {
    building_code: 'No ERP MEGA, este é o código da FILIAL — distinto do código do projeto '
      + '(veja "Código Reduzido do Projeto") e do orçamento.',
    appropriation_level: 'Nível em que a ingestão MEGA aloca o orçamento por serviço da obra.',
  },
};

// Apenas dígitos, mascarados como CNPJ (00.000.000/0000-00) — o valor ENVIADO ao back-end é o
// que o operador digitou (com ou sem máscara); a normalização para "só dígitos" é feita lá
// (`normalizeDoc`), a máscara aqui é só apresentação.
const formatCnpjMask = (value) => {
  const digits = String(value ?? '').replace(/\D/g, '').slice(0, 14)
  const p1 = digits.slice(0, 2)
  const p2 = digits.slice(2, 5)
  const p3 = digits.slice(5, 8)
  const p4 = digits.slice(8, 12)
  const p5 = digits.slice(12, 14)
  let out = p1
  if (p2) out += `.${p2}`
  if (p3) out += `.${p3}`
  if (p4) out += `/${p4}`
  if (p5) out += `-${p5}`
  return out
}

// Validação de UX (dígitos verificadores) — espelha `isValidCnpj` de
// `core/ampli-platform-back-end/src/backoffice/utils/cnpj.js`, que continua sendo a fonte de
// verdade (esta cópia só evita uma viagem ao servidor para um erro óbvio de digitação).
const cnpjDigits = (value) => String(value ?? '').replace(/\D/g, '')

// "Carregar do MEGA" (D10): a consulta vai ao ERP pelo data-pipeline e só preenche o
// formulário — nada é gravado até o operador salvar. Mensagens por `status` da resposta.
const MEGA_LOOKUP_MESSAGES = {
  not_found: (filial) => `Nenhum projeto encontrado no MEGA para a filial ${filial}. Os campos ficaram como estavam.`,
  invalid_branch: (filial) => `A filial ${filial} não é um código válido no MEGA. Os campos ficaram como estavam.`,
  erp_unavailable: () => 'O MEGA não respondeu. Os campos ficaram como estavam; tente de novo em instantes.',
}

const megaProjectOptionLabel = (project) => [
  project.projeto_reduzido,
  project.descricao || 'Sem descrição',
  project.cnpj ? `CNPJ ${formatCnpjMask(project.cnpj)}` : 'sem CNPJ',
].join(' — ')

const isValidCnpjClient = (value) => {
  const digits = String(value ?? '').replace(/\D/g, '')
  if (digits.length !== 14 || /^(\d)\1{13}$/.test(digits)) return false
  const calc = (len) => {
    let sum = 0
    let pos = len - 7
    for (let i = 0; i < len; i++) {
      sum += Number(digits[i]) * pos--
      if (pos < 2) pos = 9
    }
    const r = sum % 11
    return r < 2 ? 0 : 11 - r
  }
  return calc(12) === Number(digits[12]) && calc(13) === Number(digits[13])
}

// Disclaimer comum aos campos que aceitam uma lista de IDs: em branco = traz tudo; preenchido
// = traz só o que foi informado; múltiplos valores sempre separados por vírgula, sem espaço.
const idListHint = (whatIfBlank) => (
  `Se deixar em branco, traz ${whatIfBlank}. Se preencher, traz apenas o(s) informado(s). `
  + `Para mais de um valor, separe por vírgula, sem espaço (ex.: 12,34,56).`
);

const ID_LIST_HINTS = {
  building_constructive_units_id: idListHint('todas as unidades construtivas'),
  cost_centers_id: idListHint('todos os centros de custo'),
  building_cost_centers_id: idListHint('todos os centros de custo da obra'),
  cost_centers_id_comercial: idListHint('todos os centros de custo comerciais'),
  bill_id_to_remove: 'IDs de título (bill) a excluir da apuração financeira. Em branco = não '
    + 'exclui nenhum. Para mais de um valor, separe por vírgula, sem espaço (ex.: 12,34,56).',
};

const APPROPRIATION_LEVEL_OPTIONS = [
  { value: 'unit', label: 'Unidade' },
  { value: 'constructivecell', label: 'Célula Construtiva' },
  { value: 'step', label: 'Etapa' },
  { value: 'substep', label: 'Subetapa' },
  { value: 'service', label: 'Serviço' },
];

// "prevision"/"project" são nomes de ferramenta (Prevision, AMPLI Project), não palavras
// genéricas — mantidos como estão em vez de traduzidos.
const PLAN_TOOL_OPTIONS = ['prevision', 'project'];

// Cada campo vira uma célula própria do grid — sem isso o fieldset (grid, pro mesmo padrão
// visual do restante do formulário) intercalava label/input em colunas separadas em vez de
// empilhar label-sobre-input dentro da mesma célula.
//
// O rótulo ocupa uma linha só (D11): o texto longo é resumido com reticências e o nome
// inteiro fica no `title`; o ícone de ajuda fica fora do recorte, para o tooltip não ser
// cortado junto.
const Field = ({ label, title, full, hint, children }) => {
  const fieldId = children.props.id || children.props.name
  return (
    <div className="building-field" style={full ? { gridColumn: '1 / -1' } : undefined}>
      <label htmlFor={fieldId}>
        <span className="building-field__label" title={title || label}>{label}</span>
        {hint && <InfoTooltip text={hint} />}
      </label>
      {cloneElement(children, { id: fieldId })}
    </div>
  )
}

// O data-pipeline lê estes campos de `buildings` sem checar ausência (`cost_centers_id_comercial`
// é indexado direto em `erp_sienge/extract/extractIncome.py`). Vazio é um valor válido — o que
// quebra a ingestão é o campo NÃO EXISTIR no documento, então o formulário sempre os envia.
const emptyBuilding = {
  city: '',
  state: '',
  cost_centers_id_comercial: '',
  building_constructive_units_id: '',
  bill_id_to_remove: '',
  stock: false,
}

// `bill_id_to_remove` está persistido como ARRAY (ex.: [12]) — o input é texto separado por
// vírgula, mesmo padrão dos outros campos de lista de ids desta tela.
const idListToText = (value) => {
  // `[null]` é o "vazio" convencionado no banco (ver o comentário no envio) — na tela tem que
  // aparecer como campo em branco, não como a string "null".
  if (Array.isArray(value)) return value.filter(id => id !== null && id !== undefined).join(',')
  return value ?? ''
}

const buildingToFormState = (building) => ({
  ...building,
  company_id: `${building.company_id}`,
  city: building.city ?? '',
  state: building.state ?? '',
  cost_centers_id_comercial: building.cost_centers_id_comercial ?? '',
  bill_id_to_remove: idListToText(building.bill_id_to_remove),
  // Armazenado sem máscara (só dígitos); exibido sempre mascarado ao abrir para edição.
  cnpj: building.cnpj ? formatCnpjMask(building.cnpj) : '',
  projeto_reduzido: building.projeto_reduzido ?? '',
  projeto_id: building.projeto_id ?? '',
  start_date: building.start_date ? new Date(building.start_date).toISOString().slice(0, 10) : '',
  end_date: building.end_date ? new Date(building.end_date).toISOString().slice(0, 10) : '',
  // Só ano-mês: o campo virou seletor de mês, porque a série do índice é mensal (dia 1º).
  budget_delivery_date: building.budget_delivery_date ? new Date(building.budget_delivery_date).toISOString().slice(0, 7) : '',
  indexer_description: building.indexer?.description || '',
  indexer_type: building.indexer?.type || '',
  indexer_uf: building.indexer?.uf || '',
})

// building: opcional — quando informado, o formulário abre em modo de edição (chama
// updateBuilding em vez de createBuilding). onSuccess: opcional — chamado após
// criação/edição (usado quando o formulário é aberto dentro de um Modal).
const NewBuilding = ({ building, onSuccess }) => {
  const isEditing = !!building
  const [newBuilding, setNewBuilding] = useState(building ? buildingToFormState(building) : emptyBuilding)
  const [companies, setCompanies] = useState([])
  const [deleteConfirming, setDeleteConfirming] = useState(false)
  const [actionFeedback, setActionFeedback] = useState('')
  const [submitting, setSubmitting] = useState(false)
  // "Carregar do MEGA": `status` é idle | loading | choose | done | error. `projects` só vale
  // para a filial e a construtora em que foi buscado; `cnpjConflict` guarda o CNPJ do MEGA
  // que diverge do preenchido, à espera da confirmação do operador.
  const [megaLookup, setMegaLookup] = useState({ status: 'idle', projects: [], message: '', cnpjConflict: null })
  const { listCompanies, createBuilding, updateBuilding, deleteBuilding } = useProjectActions();

  const getCompanies = async () => {
    const res = await listCompanies();
    setCompanies([...(res?.companies || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR')))
  }

  useEffect(() => {
    getCompanies();
  }, [])

  const selectedCompany = companies.find(c => `${c._id}` === `${newBuilding.company_id}`)

  // Cidade passa a ser um dropdown dependente da UF (antes era texto livre) — evita "São Paulo"
  // vs "Sao paulo" vs "SP - Capital" pro mesmo município. A lista vem da API de localidades do
  // IBGE (município é uma lista grande demais pra empacotar no bundle, diferente das 27 UFs).
  const [cities, setCities] = useState([])
  const [citiesLoading, setCitiesLoading] = useState(false)
  const [citiesError, setCitiesError] = useState(false)
  // Guarda a UF anterior pra distinguir "acabou de montar com uma obra já cadastrada" (não
  // mexe na cidade existente) de "o operador trocou de UF" (a cidade antiga pode não pertencer
  // à UF nova, então limpa). Sem essa distinção, toda obra em edição perderia a cidade ao abrir.
  const previousStateRef = useRef(newBuilding.state || '')
  useEffect(() => {
    const uf = newBuilding.state || ''
    const previousUf = previousStateRef.current
    previousStateRef.current = uf
    if (uf !== previousUf) {
      setNewBuilding(prev => (prev.city ? { ...prev, city: '' } : prev))
    }
    if (!uf) {
      setCities([])
      setCitiesError(false)
      return undefined
    }
    let cancelled = false
    setCitiesLoading(true)
    setCitiesError(false)
    fetchCitiesByState(uf)
      .then(list => { if (!cancelled) setCities(list) })
      .catch(() => {
        if (cancelled) return
        setCitiesError(true)
        setCities([])
      })
      .finally(() => { if (!cancelled) setCitiesLoading(false) })
    return () => { cancelled = true }
  }, [newBuilding.state])

  // Uma obra já cadastrada pode ter uma cidade que não está (mais) na lista do IBGE para a UF
  // salva (erro de digitação legado, mudança administrativa) — sem isto, abrir pra editar
  // mostraria o dropdown em branco e o operador perderia o valor original ao salvar de novo.
  const cityOptions = newBuilding.city && !cities.includes(newBuilding.city)
    ? [...cities, newBuilding.city].sort((a, b) => a.localeCompare(b, 'pt-BR'))
    : cities

  const cityPlaceholder = !newBuilding.state
    ? 'Escolha a UF primeiro'
    : citiesLoading
      ? 'Carregando cidades...'
      : 'Não informado'

  // ERP da construtora selecionada é a regra primária que direciona os campos específicos da
  // obra — igual à decisão já tomada em newCompany.jsx. Sienge ganha um default editável para
  // bank_movement_id_to_remove; outros ERPs não usam esse campo, então não o pré-preenchemos.
  useEffect(() => {
    // `selectedCompany` só resolve depois que `listCompanies` retorna — antes disso não dá
    // para saber o ERP de uma obra já cadastrada (edição), então não mexemos no campo.
    if (!selectedCompany) return
    if (selectedCompany.erp === 'SIENGE') {
      if (newBuilding.bank_movement_id_to_remove === undefined) {
        setNewBuilding(prev => ({ ...prev, bank_movement_id_to_remove: DEFAULT_SIENGE_BANK_MOVEMENT_IDS }))
      }
      return
    }
    // Ao trocar para um ERP que não é Sienge, o campo é limpo do estado já aqui — não só no
    // submit (`formattedBuilding`) — para que o operador não veja um valor de Sienge
    // sobrevivendo na tela depois de trocar de construtora.
    if (newBuilding.bank_movement_id_to_remove !== undefined) {
      setNewBuilding(prev => {
        const next = { ...prev }
        delete next.bank_movement_id_to_remove
        return next
      })
    }
  }, [selectedCompany?.erp])

  // Trocar a construtora ou a filial invalida o que foi carregado do MEGA para a anterior.
  useEffect(() => {
    setMegaLookup({ status: 'idle', projects: [], message: '', cnpjConflict: null })
  }, [newBuilding.company_id, newBuilding.building_code])

  // Campos que o domínio exige em maiúsculas — ver o comentário sobre a staging zone acima.
  const UPPERCASE_FIELDS = ['company_code']

  const handleInput = (e) => {
    // O código acompanha o cadastro da construtora somente quando o operador escolhe/troca a
    // construtora. Carregar a lista durante uma edição não pode sobrescrever um código legado
    // diferente. Preferência: `company_code` da própria construtora (padronizado no cadastro
    // dela, ver newCompany.jsx) — se ainda não preenchido lá, cai para o subdomain, como já
    // era antes deste campo existir.
    if (e.target.name === 'company_id') {
      const nextCompany = companies.find(c => `${c._id}` === `${e.target.value}`)
      const nextCompanyCode = nextCompany?.company_code || nextCompany?.subdomain
      setNewBuilding(prev => ({
        ...prev,
        company_id: e.target.value,
        ...(nextCompanyCode ? { company_code: nextCompanyCode.toUpperCase() } : {}),
      }))
      return
    }
    if (e.target.name === 'cnpj') {
      setNewBuilding(prev => ({ ...prev, cnpj: formatCnpjMask(e.target.value) }))
      return
    }
    // O `projeto_id` gravado pelo registro inicial pertence ao código gravado junto: trocar o
    // código à mão desfaz o vínculo (o back-end recusa id e código de projetos diferentes), e
    // voltar ao código original o restaura.
    if (e.target.name === 'projeto_reduzido') {
      const value = e.target.value
      const loaded = megaLookup.projects.find(project => project.projeto_reduzido === value.trim())
      const savedId = loaded?.projeto_id
        ?? (value.trim() === String(building?.projeto_reduzido ?? '').trim()
          ? (building?.projeto_id ?? '')
          : '')
      setNewBuilding(prev => ({ ...prev, projeto_reduzido: value, projeto_id: savedId }))
      return
    }
    if (e.target.type === 'number') {
      setNewBuilding(prev => ({ ...prev, [e.target.name]: +e.target.value }))
    }
    else {
      const value = UPPERCASE_FIELDS.includes(e.target.name)
        ? e.target.value.toUpperCase()
        : e.target.value
      setNewBuilding(prev => ({ ...prev, [e.target.name]: value }))
    }
  }

  const isMega = selectedCompany?.erp === 'MEGA'

  // O campo é numérico: apagado, ele vira 0 no estado — que também conta como "sem filial".
  const megaFilial = String(newBuilding.building_code ?? '').trim()
  const megaLoadUnavailableReason = !isMega
    ? 'Disponível só para construtora MEGA.'
    : !megaFilial || megaFilial === '0'
      ? 'Preencha o Código da Obra (a filial) para carregar o projeto do MEGA.'
      : null

  // Preenche código reduzido e ID do projeto. O CNPJ entra só se o campo estiver vazio; um
  // CNPJ diferente do MEGA fica como está até o operador confirmar a troca.
  const applyMegaProject = (project) => {
    const currentCnpj = cnpjDigits(newBuilding.cnpj)
    const megaCnpj = cnpjDigits(project.cnpj)
    const conflict = Boolean(megaCnpj && currentCnpj && currentCnpj !== megaCnpj)
    setNewBuilding(prev => ({
      ...prev,
      projeto_reduzido: project.projeto_reduzido ?? '',
      projeto_id: project.projeto_id ?? '',
      ...(megaCnpj && !currentCnpj ? { cnpj: formatCnpjMask(megaCnpj) } : {}),
    }))
    setMegaLookup(prev => ({
      ...prev,
      status: 'done',
      message: `Projeto ${project.projeto_reduzido} carregado do MEGA. Salve para gravar na obra.`,
      cnpjConflict: conflict ? megaCnpj : null,
    }))
  }

  const loadFromMega = async () => {
    if (megaLoadUnavailableReason || megaLookup.status === 'loading') return
    setMegaLookup({ status: 'loading', projects: [], message: '', cnpjConflict: null })
    try {
      const result = await fetchMegaProjects(newBuilding.company_id, megaFilial)
      const projects = Array.isArray(result?.projects) ? result.projects : []
      if (result?.status === 'ok' && projects.length === 1) {
        setMegaLookup({ status: 'done', projects, message: '', cnpjConflict: null })
        applyMegaProject(projects[0])
        return
      }
      if (result?.status === 'ok' && projects.length > 1) {
        setMegaLookup({
          status: 'choose',
          projects,
          message: `A filial ${megaFilial} tem ${projects.length} projetos no MEGA. Escolha o desta obra.`,
          cnpjConflict: null,
        })
        return
      }
      const describe = MEGA_LOOKUP_MESSAGES[result?.status] ?? MEGA_LOOKUP_MESSAGES.not_found
      setMegaLookup({ status: 'error', projects: [], message: describe(megaFilial), cnpjConflict: null })
    } catch (error) {
      setMegaLookup({
        status: 'error',
        projects: [],
        message: error?.code === 'timeout' || error?.code === 'network_error'
          ? MEGA_LOOKUP_MESSAGES.erp_unavailable()
          : `${error?.message || 'Não foi possível consultar o MEGA.'} Os campos ficaram como estavam.`,
        cnpjConflict: null,
      })
    }
  }

  const handleMegaProjectChoice = (e) => {
    const project = megaLookup.projects[Number(e.target.value)]
    if (project) applyMegaProject(project)
  }

  const resolveCnpjConflict = (useMega) => {
    if (useMega && megaLookup.cnpjConflict) {
      setNewBuilding(prev => ({ ...prev, cnpj: formatCnpjMask(megaLookup.cnpjConflict) }))
    }
    setMegaLookup(prev => ({ ...prev, cnpjConflict: null }))
  }

  const hasRequiredFields = REQUIRED_FIELDS.every(field => {
    const value = newBuilding[field]
    return value !== undefined && value !== '' && value !== 'choose'
  })

  // Validação de UX: só bloqueia o envio por CNPJ malformado quando o campo já tem algo
  // digitado — campo vazio já é coberto por `hasRequiredFields`, e a mensagem de "obrigatório"
  // não deve competir com a de "inválido".
  const cnpjInvalid = isMega && newBuilding.cnpj && !isValidCnpjClient(newBuilding.cnpj)

  // O cadastro não é mais bloqueado por teste de conexão: a conectividade com o ERP é atributo
  // da CONSTRUTORA e é testada no formulário dela (`newCompany.jsx`). Aqui o gate seguinte é a
  // primeira execução do pipeline, que roda depois de salvar e valida o cadastro de verdade.
  const sendDisabled = !hasRequiredFields || cnpjInvalid || submitting

  const handleSend = async (e) => {
    e.preventDefault()
    if (sendDisabled) return
    setSubmitting(true)
    setActionFeedback('')
    try {
      const formattedBuilding = {
        ...newBuilding,
        start_date: new Date(newBuilding.start_date),
        end_date: new Date(newBuilding.end_date),
        // `YYYY-MM` -> dia 1º em UTC. Sem o `-01` explícito, `new Date('2026-01')` já daria dia 1º,
        // mas deixar implícito esconderia a invariante de quem ler depois.
        budget_delivery_date: new Date(`${newBuilding.budget_delivery_date}-01T00:00:00.000Z`),
        company_code: (newBuilding.company_code || '').toUpperCase(),
        indexer: {
          description: newBuilding.indexer_description,
          type: newBuilding.indexer_type,
          uf: newBuilding.indexer_uf,
        },
        scope: FIXED_SCOPE,
        // Persistido como array — o input é texto separado por vírgula. ⚠️ Vazio NÃO pode virar
        // `[]`: o pipeline monta o contexto da obra com `pd.DataFrame(buildingData)`, e o pandas
        // alinha todas as colunas ao tamanho da única que é lista — com `[]` o DataFrame vai a
        // zero linhas e a ingestão morre com `KeyError: 0` no `load`. `[null]` é a convenção do
        // dado real (uma linha, nulo = "não excluir nenhum título").
        bill_id_to_remove: (() => {
          const ids = String(newBuilding.bill_id_to_remove ?? '')
            .split(',')
            .map(id => id.trim())
            .filter(Boolean)
            .map(Number)
            .filter(id => Number.isFinite(id))
          return ids.length > 0 ? ids : [null]
        })(),
        // Sempre enviados, mesmo vazios/falsos: o pipeline indexa estes campos sem guarda de
        // ausência (`.loc[0, 'building_constructive_units_id'].split(",")` em
        // `erp_sienge/transform/transformBuildingCostEstimationsItems.py`), e campo AUSENTE não
        // vira coluna no json_normalize → KeyError. Vazio é válido e significa "traz tudo".
        cost_centers_id_comercial: newBuilding.cost_centers_id_comercial ?? '',
        building_constructive_units_id: newBuilding.building_constructive_units_id ?? '',
        stock: newBuilding.stock === true,
      }
      delete formattedBuilding.indexer_description
      delete formattedBuilding.indexer_type
      delete formattedBuilding.indexer_uf
      // Estes campos só existem para Sienge (o back-end também os descarta pra qualquer outro
      // ERP — ver ERP_EXCLUSIVE_FIELDS em createBuilding.js/updateBuilding.js) — se o operador
      // trocar de construtora para uma não-Sienge depois de preenchido, não envia nenhum deles.
      if (selectedCompany?.erp !== 'SIENGE') {
        delete formattedBuilding.bank_movement_id_to_remove
        delete formattedBuilding.bill_id_to_remove
        delete formattedBuilding.cost_centers_id
        delete formattedBuilding.cost_centers_id_comercial
        delete formattedBuilding.building_cost_centers_id
        delete formattedBuilding.building_constructive_units_id
        delete formattedBuilding.budget_version
        delete formattedBuilding.plan_tool
        delete formattedBuilding.scope
      }
      if (!isMega) delete formattedBuilding.projeto_id

      const result = isEditing
        ? await updateBuilding({ ...formattedBuilding, building_id: building._id })
        : await createBuilding(formattedBuilding)

      if (result?.ok && !isEditing) {
        setNewBuilding(emptyBuilding)
      }
      if (!result?.ok) {
        setActionFeedback(result?.message || 'Não foi possível salvar a obra.')
      } else {
        setActionFeedback('')
      }
      onSuccess?.(result)
    } catch {
      setActionFeedback('Não foi possível salvar a obra.')
    } finally {
      setSubmitting(false)
    }
  }

  // (Des)ativar é reversível — Remover é definitivo (não toca o histórico de medição em
  // `wbs_buildings`, só o cadastro em `buildings`).
  const toggleActive = async () => {
    const active = building.active === false
    const result = await updateBuilding({ ...building, building_id: building._id, active })
    if (result?.ok) onSuccess?.(result)
    else setActionFeedback(result?.message || 'Não foi possível alterar o status.')
  }

  const confirmDelete = async () => {
    const result = await deleteBuilding(building._id)
    if (result?.ok) {
      onSuccess?.(result)
    } else {
      setActionFeedback(result?.message || 'Não foi possível remover.')
      setDeleteConfirming(false)
    }
  }

  const getLabel = (name) => FIELD_LABELS[name] || name.split("_").map(
    (word) => word[0].toUpperCase() + word.substring(1)
  ).join(" ")

  // Combina o disclaimer comum (lista de IDs) com a orientação específica do ERP selecionado,
  // quando os dois existirem para o mesmo campo (ex.: `cost_centers_id_comercial` em MEGA).
  const fieldHint = (name) => {
    const erpHint = ERP_FIELD_HINTS[selectedCompany?.erp]?.[name]
    const idHint = ID_LIST_HINTS[name]
    if (erpHint && idHint) return `${idHint} ${erpHint}`
    return erpHint || idHint
  }

  const fields = [{
    name: 'building_code',
    type: 'number',
  }, {
    name: 'company_code',
    type: 'text',
  }, {
    name: 'building_name',
    type: 'text',
  }, {
    name: 'state',
    type: 'select',
    optional: true,
    options: BRAZILIAN_STATES.map(({ uf, name }) => ({ value: uf, label: `${uf} — ${name}` })),
  }, {
    name: 'start_date',
    type: 'date',
  }, {
    name: 'end_date',
    type: 'date',
  }, {
    name: 'area',
    type: 'number',
  }, {
    // Os cinco marcados `siengeOnly` abaixo são conceitos/gambiarras do pipeline Sienge sem
    // nenhuma leitura em erp_mega/* (field-audit.md) — o back-end já não persiste nenhum deles
    // pra obra MEGA (`ERP_EXCLUSIVE_FIELDS` em createBuilding.js/updateBuilding.js); ocultar
    // aqui evita o operador preencher um campo que será descartado silenciosamente.
    name: 'budget_version',
    type: 'number',
    siengeOnly: true,
  }, {
    name: 'building_constructive_units_id',
    type: 'text',
    siengeOnly: true,
  }, {
    name: 'cost_centers_id',
    type: 'text',
    siengeOnly: true,
  }, {
    name: 'cost_centers_id_comercial',
    type: 'text',
    siengeOnly: true,
  }, {
    name: 'building_cost_centers_id',
    type: 'text',
    siengeOnly: true,
  }, {
    name: 'appropriation_level',
    type: 'select',
    options: APPROPRIATION_LEVEL_OPTIONS,
  }, {
    name: 'plan_tool',
    type: 'select',
    options: PLAN_TOOL_OPTIONS.map(value => ({ value, label: value })),
    siengeOnly: true,
  },
  ].filter(field => !field.siengeOnly || selectedCompany?.erp !== 'MEGA')

  return (
    <>
      <div className="backoffice-form-card">
        <h2>{isEditing ? 'Editar Obra' : 'Nova Obra'}</h2>
        <form className="new-building-container">
          <div>
            <label>Construtora</label>
            <select name="company_id" value={newBuilding.company_id || 'choose'} onChange={handleInput}>
              <option disabled value='choose'>Choose</option>
              {companies.map(i => {
                return <option key={i._id} value={`${i._id}`}>{i.name}</option>
              })}
            </select>
          </div>
          {fields.map(i => {
            const hint = fieldHint(i.name)
            if (i.name === 'state') {
              // UF primeiro (esquerda), Cidade depois (direita) — a cidade depende da UF, não
              // o contrário, então a ordem de leitura segue a ordem de dependência.
              return <Fragment key="city-state">
                <Field key={i.name} label={getLabel(i.name)} hint={hint}>
                  <select
                    name={i.name}
                    value={newBuilding[i.name] || (i.optional ? '' : 'choose')}
                    onChange={handleInput}
                  >
                    <option disabled={!i.optional} value={i.optional ? '' : 'choose'}>
                      {i.optional ? 'Não informado' : 'Choose'}
                    </option>
                    {i.options.map(j => {
                      return <option key={j.value} value={j.value}>{j.label}</option>
                    })}
                  </select>
                </Field>
                <Field
                  label="Cidade"
                  hint="A lista de cidades depende da UF escolhida ao lado."
                >
                  <select
                    name="city"
                    value={newBuilding.city || ''}
                    onChange={handleInput}
                    disabled={!newBuilding.state || citiesLoading}
                  >
                    <option value="">{cityPlaceholder}</option>
                    {cityOptions.map(cityName => (
                      <option key={cityName} value={cityName}>{cityName}</option>
                    ))}
                  </select>
                </Field>
                {citiesError && (
                  <p
                    className="reset-password-feedback"
                    role="alert"
                    style={{ gridColumn: '1 / -1', marginTop: 0 }}
                  >
                    Não foi possível carregar as cidades desta UF.
                  </p>
                )}
              </Fragment>
            }
            if (i.type === 'select') {
              return <Field key={i.name} label={getLabel(i.name)} hint={hint}>
                <select
                  name={i.name}
                  value={newBuilding[i.name] || (i.optional ? '' : 'choose')}
                  onChange={handleInput}
                >
                  <option disabled={!i.optional} value={i.optional ? '' : 'choose'}>
                    {i.optional ? 'Não informado' : 'Choose'}
                  </option>
                  {i.options.map(j => {
                    return <option key={j.value} value={j.value}>{j.label}</option>
                  })}
                </select>
              </Field>
            } else {
              return <Field key={i.name} label={getLabel(i.name)} hint={hint}>
                <input type={i.type} name={i.name} onChange={handleInput} value={newBuilding[i.name] ?? ''} />
              </Field>
            }
          })}

          <fieldset className="indexer-fieldset">
            <legend>Índice de correção</legend>
            <Field label="Índice">
              <select name="indexer_description" onChange={handleInput} value={newBuilding.indexer_description || ''}>
                <option disabled value=''>Choose</option>
                {INDEXER_FAMILIES.map(option => <option key={option} value={option}>{option}</option>)}
              </select>
            </Field>

            {newBuilding.indexer_description === 'CUB' && (
              <>
                <Field label="Categoria (CUB)">
                  <select name="indexer_type" onChange={handleInput} value={newBuilding.indexer_type || ''}>
                    <option disabled value=''>Choose</option>
                    {CUB_CATEGORIES.map(option => <option key={option} value={option}>{option}</option>)}
                  </select>
                </Field>
                <Field label="UF (Sinduscon)">
                  <select name="indexer_uf" onChange={handleInput} value={newBuilding.indexer_uf || ''}>
                    <option disabled value=''>Choose</option>
                    {BRAZILIAN_STATES.map(({ uf, name }) => <option key={uf} value={uf}>{uf} — {name}</option>)}
                  </select>
                </Field>
              </>
            )}

            {newBuilding.indexer_description === 'INCC' && (
              <Field label="Categoria (opcional)">
                <input type="text" name="indexer_type" onChange={handleInput} value={newBuilding.indexer_type || ''} />
              </Field>
            )}

            <Field
              label="Mês de Entrega do Orçamento"
              hint={'O índice de correção (CUB/INCC) usa este mês como referência — por isso os dois '
                + 'campos ficam juntos. A série do índice é mensal, então só o mês é escolhido e a '
                + 'data é gravada sempre no dia 1º.'}
            >
              {/* `type="month"` em vez de `type="date"`: torna IMPOSSÍVEL escolher outro dia. O
                  pipeline busca o índice-base por igualdade exata em `date_used_in`, e a série
                  só tem dia 1º — qualquer outro dia derrubava a execução em milissegundos. */}
              <input type="month" name="budget_delivery_date" onChange={handleInput} value={newBuilding.budget_delivery_date || ''} />
            </Field>
          </fieldset>

          {selectedCompany?.erp === 'SIENGE' && (
            <Field
              label={getLabel('bank_movement_id_to_remove')}
              hint="IDs de tipo de movimento bancário Sienge a excluir da reconciliação. Preenchido com o padrão usado nas demais construtoras Sienge — ajuste se necessário."
            >
              <input
                type="text"
                name="bank_movement_id_to_remove"
                onChange={handleInput}
                value={newBuilding.bank_movement_id_to_remove ?? DEFAULT_SIENGE_BANK_MOVEMENT_IDS}
              />
            </Field>
          )}

          {isMega && (
            <>
              <Field
                label={getLabel('cnpj')}
                hint="CNPJ da própria obra (não o da construtora), usado pelas rotas da API MEGA de Pedidos e Contratos. Pode ficar em branco: o registro inicial (primeira execução) o preenche com o CNPJ da filial no MEGA. Armazenado sem máscara."
              >
                <input
                  type="text"
                  name="cnpj"
                  inputMode="numeric"
                  onChange={handleInput}
                  value={newBuilding.cnpj ?? ''}
                  aria-invalid={cnpjInvalid || undefined}
                />
              </Field>
              {cnpjInvalid && (
                <p className="reset-password-feedback" style={{ gridColumn: '1 / -1' }} role="alert">
                  CNPJ inválido.
                </p>
              )}
              <Field
                label={getLabel('projeto_reduzido')}
                title={FIELD_FULL_LABELS.projeto_reduzido}
                hint="Código do PROJETO no ERP MEGA — diferente do Código da Obra (a filial) e do orçamento. Pode ficar em branco: o registro inicial o busca no MEGA pela filial. Use “Carregar do MEGA” para preencher agora, inclusive quando a filial tiver mais de um projeto."
              >
                <input
                  type="text"
                  name="projeto_reduzido"
                  onChange={handleInput}
                  value={newBuilding.projeto_reduzido ?? ''}
                />
              </Field>
              <Field
                label={getLabel('projeto_id')}
                title={FIELD_FULL_LABELS.projeto_id}
                hint="Identificador do projeto no MEGA (base64 de padrão, identificador e código reduzido). Preenchido pelo registro inicial; fica vazio quando o código é trocado à mão e volta a ser completado na próxima atualização completa."
              >
                <input
                  type="text"
                  name="projeto_id"
                  readOnly
                  value={newBuilding.projeto_id ?? ''}
                />
              </Field>
              <div className="mega-project-loader" style={{ gridColumn: '1 / -1' }}>
                <button
                  type="button"
                  className="button"
                  onClick={loadFromMega}
                  disabled={Boolean(megaLoadUnavailableReason) || megaLookup.status === 'loading'}
                  aria-busy={megaLookup.status === 'loading' || undefined}
                  title={megaLoadUnavailableReason || undefined}
                  aria-describedby="mega-project-loader-status"
                >
                  {megaLookup.status === 'loading' ? 'Consultando o MEGA...' : 'Carregar do MEGA'}
                </button>
                <p id="mega-project-loader-status" className="mega-project-loader__status" role="status">
                  {megaLoadUnavailableReason
                    || (megaLookup.status === 'error' ? '' : megaLookup.message)
                    || 'Busca o projeto da filial no MEGA e preenche os campos acima; nada é gravado até salvar.'}
                </p>
                {megaLookup.status === 'error' && (
                  <p className="reset-password-feedback" role="alert">{megaLookup.message}</p>
                )}
                {megaLookup.status === 'choose' && (
                  <Field label="Projeto MEGA da filial">
                    <select name="mega_project" defaultValue="" onChange={handleMegaProjectChoice}>
                      <option value="" disabled>Escolha o projeto</option>
                      {megaLookup.projects.map((project, index) => (
                        <option key={`${project.projeto_id}|${index}`} value={String(index)}>
                          {megaProjectOptionLabel(project)}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
                {megaLookup.cnpjConflict && (
                  <div className="reset-password-confirm" role="alert">
                    <p>
                      O CNPJ da obra ({newBuilding.cnpj}) é diferente do CNPJ do projeto no MEGA
                      ({formatCnpjMask(megaLookup.cnpjConflict)}).
                    </p>
                    <button type="button" className="button button-accent" onClick={() => resolveCnpjConflict(true)}>
                      Usar o CNPJ do MEGA
                    </button>
                    <button type="button" className="button" onClick={() => resolveCnpjConflict(false)}>
                      Manter o CNPJ atual
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Exclusivo de Sienge: exclusão de título na apuração financeira. Não lido por
              erp_mega/* — o back-end também não persiste este campo pra obra MEGA. */}
          {selectedCompany?.erp !== 'MEGA' && (
            <Field
              label={getLabel('bill_id_to_remove')}
              hint={ID_LIST_HINTS.bill_id_to_remove}
            >
              <input
                type="text"
                name="bill_id_to_remove"
                onChange={handleInput}
                value={newBuilding.bill_id_to_remove ?? ''}
              />
            </Field>
          )}

          <div className="checkbox-row" style={{ gridColumn: '1 / -1' }}>
            <label>
              <input
                type="checkbox"
                checked={newBuilding.stock === true}
                onChange={(e) => setNewBuilding(prev => ({ ...prev, stock: e.target.checked }))}
              />
              Obra com controle de estoque
              <InfoTooltip text="Habilita a ingestão e a atualização de estoque para esta obra. Sem isso, a atualização de estoque não fica disponível no dashboard." />
            </label>
          </div>

          {/* A PRIMEIRA execução (e a liberação inicial de acesso) fica no botão "Registrar
              dados" da listagem de obras, não aqui — é o passo que se faz uma vez. Este painel
              é a manutenção depois disso: precisa da obra já persistida E já ingerida, porque
              grava o `_id` de `wbs_buildings` em `users.buildingIds`. */}
          {isEditing && (
            <div style={{ gridColumn: '1 / -1' }}>
              <BuildingAccessPanel buildingId={building._id} />
            </div>
          )}

          {isEditing && building.active === false && (
            <p className="inactive-badge">Esta obra está desativada.</p>
          )}

          {deleteConfirming && (
            <div className="reset-password-confirm">
              <p>Remover definitivamente "{building.building_name || building.description}"? Essa ação não pode ser desfeita.</p>
              <button type="button" className="button button-red" onClick={confirmDelete}>Confirmar remoção</button>
              <button type="button" className="button" onClick={() => setDeleteConfirming(false)}>Cancelar</button>
            </div>
          )}
          {actionFeedback && (
            <p className="reset-password-feedback" role="alert" aria-live="assertive">
              {actionFeedback}
            </p>
          )}

          <div className="edit-user-actions">
            <button
              disabled={sendDisabled}
              className="button button-accent"
              onClick={handleSend}
              aria-busy={submitting}
            >
              {submitting ? 'Salvando...' : (isEditing ? 'Salvar' : 'Enviar')}
            </button>
            {isEditing && (
              <>
                <button type="button" className="button" onClick={toggleActive}>
                  {building.active === false ? 'Ativar' : 'Desativar'}
                </button>
                <button type="button" className="button button-red" onClick={() => setDeleteConfirming(true)}>Remover</button>
              </>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default NewBuilding;
